if (!customElements.get('cart-note')) {
  class CartNote extends HTMLElement {
    // A shared queue survives drawer merges and keeps older responses from
    // overwriting a newer draft. The saved note remains Shopify-authoritative.
    static instances = new Set();
    static savedValue = null;
    static draftValue = null;
    static timer = null;
    static saving = null;
    static checkoutPending = false;

    connectedCallback() {
      this.textarea = this.querySelector('textarea');
      if (!this.textarea) return;
      if (!CartNote.dirty() && !CartNote.saving) {
        CartNote.savedValue = this.textarea.value;
        CartNote.draftValue = this.textarea.value;
      } else {
        this.textarea.value = CartNote.draftValue;
      }
      CartNote.instances.add(this);
      this.handleInput = () => {
        CartNote.draftValue = this.textarea.value;
        CartNote.instances.forEach((note) => {
          if (note !== this) note.textarea.value = CartNote.draftValue;
        });
        clearTimeout(CartNote.timer);
        CartNote.timer = setTimeout(() => CartNote.flush().catch(() => {}), 300);
      };
      this.addEventListener('input', this.handleInput);
    }

    disconnectedCallback() {
      this.removeEventListener('input', this.handleInput);
      CartNote.instances.delete(this);
    }

    static dirty() {
      return CartNote.draftValue !== CartNote.savedValue;
    }

    static showStatus(message) {
      CartNote.instances.forEach((note) => {
        let status = note.querySelector('[data-cart-note-status]');
        if (!status) {
          status = document.createElement('p');
          status.className = 'small-text';
          status.setAttribute('data-cart-note-status', '');
          status.setAttribute('role', 'status');
          note.appendChild(status);
        }
        status.textContent = message;
      });
    }

    static flush() {
      clearTimeout(CartNote.timer);
      if (CartNote.saving) return CartNote.saving;
      if (!CartNote.dirty()) return Promise.resolve();
      CartNote.showStatus('Saving order note…');
      CartNote.saving = (async () => {
        while (CartNote.dirty()) {
          const value = CartNote.draftValue;
          const response = await fetch(theme.routes.cartUpdate, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: value })
          });
          if (!response.ok) throw new Error('Note save failed');
          CartNote.savedValue = value;
        }
        CartNote.showStatus('Order note saved');
      })().catch((error) => {
        CartNote.showStatus('Your order note could not be saved. Please try checkout again to retry.');
        throw error;
      }).finally(() => { CartNote.saving = null; });
      return CartNote.saving;
    }
  }

  customElements.define('cart-note', CartNote);

  // Replay the original control after saving so native form submission,
  // locale-aware destinations and the existing terms check still run.
  const waitForNote = (evt, resume) => {
    if (!CartNote.dirty() && !CartNote.saving) return;
    evt.preventDefault();
    evt.stopImmediatePropagation();
    if (CartNote.checkoutPending) return;
    CartNote.checkoutPending = true;
    CartNote.flush().then(resume).catch(() => {}).finally(() => { CartNote.checkoutPending = false; });
  };
  document.addEventListener('click', (evt) => {
    const control = evt.target.closest('cart-form .checkout-buttons a, cart-form [name="checkout"], cart-form .additional-checkout-buttons input');
    if (control) waitForNote(evt, () => { if (control.isConnected) control.click(); });
  }, true);
  document.addEventListener('submit', (evt) => {
    if (evt.target.id === 'cartform') {
      const form = evt.target;
      const submitter = evt.submitter;
      waitForNote(evt, () => { if (form.isConnected) form.requestSubmit(submitter || undefined); });
    }
  }, true);
}
